import React from "react";
import { Sparkles, Heart, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ExhibitionEmptyStateProps {
  variant?: "no-stages" | "quiet" | "starting-soon";
  watchersCount?: number;
  onHostStage?: () => void;
  onExplore?: () => void;
}

const ExhibitionEmptyState: React.FC<ExhibitionEmptyStateProps> = ({
  variant = "quiet",
  watchersCount = 0,
  onHostStage,
  onExplore,
}) => {
  return (
    <div className="rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 p-8 text-center relative overflow-hidden">
      {/* Ambient background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-4 left-8 animate-pulse opacity-30">
          <Heart className="w-6 h-6 text-pink-400 fill-pink-400" />
        </div>
        <div className="absolute bottom-6 right-6 float-animation opacity-20">
          <Sparkles className="w-8 h-8 text-amber-400" />
        </div>
        <div className="absolute top-1/2 left-4 float-animation-delayed opacity-20">
          <Heart className="w-4 h-4 text-rose-400 fill-rose-400" />
        </div>
      </div>

      <div className="relative z-10">
        {variant === "starting-soon" ? (
          <>
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center">
              <Clock className="w-7 h-7 text-pink-500" />
            </div>
            <h3 className="font-bold text-foreground text-lg mb-2">
              Stages starting soon
            </h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-4">
              The festival is warming up. Be the first to start a live stage!
            </p>
          </>
        ) : variant === "quiet" ? (
          <>
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-purple-500" />
            </div>
            <h3 className="font-bold text-foreground text-lg mb-2">
              It's quiet here...
            </h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-4">
              {watchersCount > 0 
                ? `${watchersCount} people are exploring. Start a stage and meet them!`
                : "Start a live stage and others will join you."
              }
            </p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center">
              <Heart className="w-7 h-7 text-pink-500" />
            </div>
            <h3 className="font-bold text-foreground text-lg mb-2">
              No live stages yet
            </h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-4">
              Be the first to host and welcome others!
            </p>
          </>
        )}

        {/* Watchers indicator */}
        {watchersCount > 0 && (
          <div className="flex items-center justify-center gap-2 mb-4 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{watchersCount} explorers watching</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
          {onHostStage && (
            <Button
              onClick={onHostStage}
              className={cn(
                "bg-gradient-to-r from-pink-500 to-rose-500",
                "hover:from-pink-600 hover:to-rose-600",
                "text-white font-semibold pulse-glow"
              )}
            >
              <Heart className="w-4 h-4 mr-2 fill-white" />
              Be the first to host
            </Button>
          )}
          {onExplore && (
            <Button
              onClick={onExplore}
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
            >
              Explore other cities
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExhibitionEmptyState;
